import React from 'react'
import { Box } from '@mantine/core'
import AppText from './AppText'
import { getProfileInitials, TIER_BORDER, TOP_TIER_NAMES } from './profileUtils'

export type ProfileUserAvatarProps = {
  imageUrl?: string | null;
  displayName?: string | null;
  username?: string | null;
  tier?: string | null;
  size?: number;
  onClick?: () => void;
  /** Profile header uses gold outline ring; list rows use tier-colored border only. */
  variant?: 'header' | 'row' | 'discover';
}

export default function ProfileUserAvatar ({
  imageUrl,
  displayName,
  username,
  tier,
  size = 44,
  onClick,
  variant = 'row'
}: ProfileUserAvatarProps) {
  const borderColor = TIER_BORDER[tier ?? 'Newcomer'] ?? '#8B949E'
  const isTopTier = TOP_TIER_NAMES.has((tier ?? '') as 'Armorer' | 'Curator')
  const initials = getProfileInitials(displayName, username)
  const fontSize = Math.round(size * 0.36)

  const headerRing =
    variant === 'header'
      ? {
          border: '4px solid var(--gs-bg)',
          outline: '3px solid var(--gs-gold)',
          boxShadow: '0 0 0 2px rgba(13,17,23,0.9)'
        }
      : {
          border: `2px solid ${borderColor}`
        }

  const discoverFallback =
    variant === 'discover' && !imageUrl
      ? {
          background: isTopTier
            ? 'linear-gradient(135deg, var(--gs-accent), var(--gs-gold))'
            : 'var(--gs-surface)',
          color: isTopTier ? '#fff' : 'var(--gs-text)'
        }
      : {
          background: 'linear-gradient(135deg, #E85D04, #C9A84C)',
          color: '#fff'
        }

  return (
    <Box
      onClick={onClick}
      sx={{
        flexShrink: 0,
        width: size,
        height: size,
        borderRadius: '50%',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : undefined,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...(imageUrl
          ? {
              background: `url(${imageUrl}) center/cover`,
              ...headerRing
            }
          : {
              ...discoverFallback,
              ...headerRing
            })
      }}
    >
      {!imageUrl && (
        <AppText
          sx={{
            fontFamily: 'Oswald, sans-serif',
            fontWeight: 700,
            fontSize,
            color: 'inherit',
            lineHeight: 1
          }}
        >
          {initials}
        </AppText>
      )}
    </Box>
  )
}
